import { Component, DoCheck, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { concat, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { PHONE_CODE } from 'src/app/core/const.variables';
import { slideInOut } from '../../animations/slide-in-out.animation';
import { getWindow } from 'ssr-window';
import { opacityInOut } from '../../animations/opacity-in-out.animation';

@Component({
  selector: 'a-select-location',
  templateUrl: 'a-select-location.component.html',
  styleUrls: ['a-select-location.component.scss'],
  animations: [slideInOut, opacityInOut]
})
export class ASelectLocationComponent implements OnInit, OnDestroy, DoCheck {

  @ViewChild('scrolledEl') scrolledEl: ElementRef | undefined;
  @ViewChild('selectEl') selectEl: ElementRef | undefined;
  @Input() isSubmitted = true;
  @Input() errorMessage = null;
  @Input() label = '';
  @Input() grayBg = true;
  @Input() hasClear = false;
  @Input() isOptional = false;
  @Input() isMultiple = false;
  @Input() isDisabled = false;
  @Input() selectedCountry = null;
  @Input() control: FormControl = new FormControl();
  @Input() items: any[] = null;
  @Output() onBlur = new EventEmitter<any>();
  
  autocomplete;
  destroy$ = new Subject<boolean>();
  searchInput$ = new Subject<string | null>();
  searchVariants$ = concat(of([]), this.searchInput$.pipe(
    switchMap((term: string) => {
      if (this.items) {
        const filtered = this.items.filter(item => 
          !term || (item.value || item).toLowerCase().includes(term.toLowerCase())
        ).map(item => typeof item === 'string' ? { value: item } : item);
        return of(filtered);
      }
      return term ? this.getPlacePredictions({ input: term }) : of([]);
    })
  )).pipe(takeUntil(this.destroy$));
  showItems = false;
  inputFocused = false;
  openPositionTop = false;
  selectedItem;
  searchValue = '';
  showLangNotify = false;
  private lastControlValue: unknown;

  constructor() {
  }

  ngOnInit(): void {     
    this.autocomplete = new google.maps['places'].AutocompleteService();
    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(vals => {
      this.lastControlValue = vals;
      this.setSelectedValues(vals);
    }) 
    this.lastControlValue = this.control.value;
    this.setSelectedValues(this.control.value, false);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngDoCheck(): void {
    const controlValue = this.control?.value;

    if (controlValue !== this.lastControlValue) {
      this.lastControlValue = controlValue;
      this.setSelectedValues(controlValue, false);
    }
  }

  onFocus() {
    if (this.isSelectionDisabled) {
      return;
    }

    this.searchValue = null;
    this.searchInput$.next(null);
    this.showDropdown();
  }

  onBlurAction() {
    if (this.isMultiple) {
      this.selectEl.nativeElement.value = null;
    } else {
      this.searchValue = this.control.value;
    }
  }

  showDropdown(val: boolean = true) {
    if (this.isSelectionDisabled) {
      this.showItems = false;
      return;
    }

    this.showItems = val;
    if (this.selectEl) {
      const position = this.selectEl.nativeElement.getBoundingClientRect().top;
      this.openPositionTop = getWindow().innerHeight - position < 240;
    }
  }

  selectItem(val: any) {
    if (this.isSelectionDisabled) {
      return;
    }

    if (val) {
      if (this.isMultiple) {
        const items = Array.isArray(this.selectedItem) && this.selectedItem.length ? [...this.selectedItem] : [];

        const foundIndex = items.findIndex(v => v === val.value);
        foundIndex === -1 ? items.push(val.value) : items.splice(foundIndex, 1);
        this.selectedItem = items.length ? items : null;
      } else {
        this.selectedItem = val.value;
      }
      this.control.setValue(this.selectedItem);
      this.control.markAsDirty();
      this.control.markAsTouched();
      this.onBlur.emit();
      this.showDropdown(false);
    }
  }

  removeSelected(val) {
    if (this.isSelectionDisabled || !this.isMultiple) {
      return;
    }

    const items = this.selectedItem.filter(v => v !== val);
    this.selectedItem = items.length ? items : null;
    this.control.setValue(this.selectedItem);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  findResult(val: any) {
    if (this.isSelectionDisabled) {
      return;
    }

    this.searchInput$.next(val);
  }

  hideNotify() {
    this.showLangNotify = false;
  }

  get isSelectionDisabled(): boolean {
    return this.isDisabled || this.control.disabled;
  }

  get selectedItems(): string[] {
    if (Array.isArray(this.selectedItem)) {
      return this.selectedItem.filter(item => !!item);
    }

    return this.selectedItem ? [this.selectedItem] : [];
  }

  inputValidator(event: any) {
    if (this.isSelectionDisabled) {
      return;
    }

    const pattern = /^[a-zA-Z0-9\s]*$/;   
    if (!pattern.test(event.target.value)) {
      event.target.value = event.target.value.replace(/[^a-zA-Z0-9\s]/g, "");
      this.showLangNotify = true;
    } else {
      this.findResult(event.target.value);
    }
  }

  clear() {
    if (this.isSelectionDisabled) {
      return;
    }

    this.selectedItem = null;
    this.showDropdown(false);
    this.control.setValue(null);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.onBlur.emit();
  }

  private setSelectedValues(val, emitSearch = true) {
    this.selectedItem = val;
    this.searchValue = this.isMultiple ? null : val;
    if (emitSearch) {
      this.searchInput$.next(this.isMultiple ? null : val);
    }
  }

  private getPlacePredictions(input): Observable<any> {
    return new Observable((observer) => {
      const request = { 
        ...input, 
        types: this.selectedCountry ? ['(cities)'] : [], 
        componentRestrictions: { 
          country: this.getCountryCode(this.selectedCountry) 
        }
      };
      this.autocomplete.getPlacePredictions(request, (places) => {
        if (places) {
          const results = [];
          if (this.selectedCountry) {
            places.map(item => results.push({ 
              value: item.structured_formatting.main_text, place_id: item.place_id 
            }));
          } else {
            places.filter(val => val.types.includes('country')).map(item => {
              results.push({ value: item.description, place_id: item.place_id });
            });
          }
          observer.next(results);
        }
      });
    });
  }

  private getCountryCode(countryName) {
    if (countryName) {
      const found = PHONE_CODE.find(item => item.name.toLowerCase() === countryName.toLowerCase());
      return found ? [found.code] : []
    }
    return [];
  } 
}
